import React from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';
import Select from 'react-select';

import Notifications from './Dental/Notifications';
import Chats from './Dental/Chats';
import PharmacyRecords from './Dental/PharmacyRecords';
import Amenities from './Dental/Amenities';
import ClientBilling from './Dental/ClientBilling';
import Insurances from './Dental/Insurances';
import BusinessProfile from './Dental/BusinessProfile';
import DentalBooking from './Dental/Bookings';
import AddVoiceGreeting from './AddVoiceGreeting';
import Advanced from './Wellness/Advanced';
import Mandatory from './Dental/Mandatory';
import Actions from './saveButtonDental';
import HiddenPreferences from './Dental/HiddenPreferences';

import { Prompt } from 'react-router-dom';
import StatusModal from './StatusModal';
import Footnote from '../Footnote';
import classnames from 'classnames';
import { Form, validation, TextareaField } from '../common';
import TextareaCounter from '../FormComponents/TextareaCounter';

import Loading from '../common/Loading';

import utils from '../../lib/utils';

import './index.scss';

const encodeData = (original, updated, formvalues) => {
  if (!original || !updated) {
    return original || {};
  }
  const validateDuration = (value) => {
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return undefined;
    }

    const validDurationPattern = /^PT(\d+H)?(\d+M)?(\d+S)?$|^PT\d+H$|^PT\d+M$|^PT\d+S$/;
    if (validDurationPattern.test(value.trim())) {
      return value.trim();
    }
    return undefined;
  };
  let paymentTypes = [];
  if (updated.payment_types && Array.isArray(updated.payment_types)) {
    paymentTypes = updated.payment_types;
  } else if (updated.paymentTypes && Array.isArray(updated.paymentTypes)) {
    paymentTypes = updated.paymentTypes;
  } else if (
    original.id &&
    original.id.billing &&
    original.id.billing.payment_types
  ) {
    paymentTypes = original.id.billing.payment_types;
  }

  const mappedPaymentTypes = paymentTypes.map((pt) => {
    if (!pt || typeof pt !== 'object') {
      return { type: '', discount: 0, processing_fee: 0 };
    }
    return {
      ...pt,
      type: pt.value || pt.type || '',
      discount: parseFloat(pt.discount || 0),
      processing_fee: parseFloat(pt.processing_fee || 0),
    };
  });

  const originalId = original.id || {};
  const originalPreferences = original.preferences || {};
  const originalBilling = originalId.billing || {};
  const originalNotifications = originalId.notifications || {};
  const originalGeneralInfo = originalId.general_information || {};
  const originalGeneral = originalId.general || {};
  const originalServices = originalId.services || {};
  const originalClientNotifications =
    original.client_notification_preferences || {};

  const encodedData = {
    ...original,
    billing: {
      ...originalBilling,
      payment_types: mappedPaymentTypes,
      payment_types_new:
        (formvalues && formvalues.paymentTypesNew) ||
        updated.paymentTypesNew ||
        [],
      insurance_types:
        (formvalues && formvalues.insuranceTypes) ||
        updated.insuranceTypes ||
        [],
      insurances_in_network:
        (formvalues && formvalues.insurances_in_network) ||
        updated.insurances_in_network ||
        [],
      special_offer:
        (formvalues && formvalues.specialOffers) || updated.specialOffers || [],
      billing_related: updated.billing_related || '',
      new_patient_consultation_billing:
        updated.new_patient_consultation_billing || '',
      cleaning_cost: updated.cleaning_cost || '',
    },
    custom_requests: updated.custom_requests || {},
    insurance: {
      accepted_insurances: updated.accepted_insurances || [],
      un_accepted_insurances: updated.un_accepted_insurances || [],
      no_insurance_info: updated.no_insurance_info || '',
      collect_insurance: updated.collect_insurance || '',
      insurance_info: updated.insurance_info || '',
    },
    notifications: {
      ...originalNotifications,
      public_contact_phones: updated.publicContactPhones || [],
      public_contact_email: updated.publicContactEmails || [],
      notify_contact_phones: updated.notifyContactPhones || [],
      notify_contact_email: updated.notifyContactEmails || [],
      emergency_message:
        (window.Config &&
          window.Config.PREFERENCE &&
          window.Config.PREFERENCE.DENTAL &&
          window.Config.PREFERENCE.DENTAL.emergency_message) ||
        originalNotifications.emergency_message ||
        '',
      enquiries: {
        sales_enquiry: {
          phones: updated.sales_enquiry_phones || [],
          emails: updated.sales_enquiry_emails || [],
        },
        misc_enquiry: {
          phones: updated.misc_enquiry_phones || [],
          emails: updated.misc_enquiry_emails || [],
        },
      },
    },
    client_notification_preferences: {
      ...originalClientNotifications,
      notification_channels: updated.notification_mode || [],
      notification_emails: updated.notification_email || [],
      notification_phones: updated.notification_phone || [],
      receive_immediately: updated.receive_immediately || [],
    },
    scheduling: {
      ...(originalId.scheduling || {}),
      ...(updated.appointments_walk_in !== undefined && {
        appointments_walk_in: updated.appointments_walk_in,
      }),
      ...(updated.cancel_appt_within_duration !== undefined && {
        cancel_appt_within_duration: updated.cancel_appt_within_duration,
      }),
      ...(updated.cancellation_charges !== undefined && {
        cancellation_charges: updated.cancellation_charges,
      }),
      ...(() => {
        const cancellationValue =
          updated.scheduling?.cancellation_duration !== undefined
            ? updated.scheduling.cancellation_duration
            : updated.cancellation_duration;

        if (cancellationValue !== undefined) {
          return { cancellation_duration: validateDuration(cancellationValue) };
        }
        return {};
      })(),
      ...(updated.cancellation_policy !== undefined && {
        cancellation_policy: updated.cancellation_policy,
      }),
      ...(updated.min_time_to_booking !== undefined && {
        min_time_to_booking: updated.min_time_to_booking,
      }),
      ...(updated.new_patient_form_link !== undefined && {
        new_patient_form_link: updated.new_patient_form_link,
      }),
      ...(updated.post_booking_instructions !== undefined && {
        post_booking_instructions: updated.post_booking_instructions,
      }),
      ...(updated.reschedule_appt_within_24hrs !== undefined && {
        reschedule_appt_within_24hrs: updated.reschedule_appt_within_24hrs,
      }),
      ...(updated.reschedule_charges !== undefined && {
        reschedule_charges: updated.reschedule_charges,
      }),
      ...(() => {
        const rescheduleValue =
          updated.scheduling?.reschedule_duration !== undefined
            ? updated.scheduling.reschedule_duration
            : updated.reschedule_duration;

        if (rescheduleValue !== undefined) {
          const validated = validateDuration(rescheduleValue);
          return { reschedule_duration: validated };
        }
        return {};
      })(),
      ...(updated.reschedule_appt_within_duration !== undefined && {
        reschedule_appt_within_duration:
          updated.reschedule_appt_within_duration,
      }),
      ...(updated.service_mapping !== undefined && {
        service_mapping: updated.service_mapping,
      }),
      ...(updated.staff_schedule_mapping !== undefined && {
        staff_schedule_mapping: updated.staff_schedule_mapping,
      }),
      ...(updated.average_booking_value !== undefined && {
        average_booking_value: updated.average_booking_value,
      }),
      ...(updated.can_reschedule_booking_online !== undefined && {
        can_reschedule_booking_online: updated.can_reschedule_booking_online,
      }),
      ...(updated.can_cancel_booking_online !== undefined && {
        can_cancel_booking_online: updated.can_cancel_booking_online,
      }),
      ...(updated.online_booking_link !== undefined && {
        online_booking_link: updated.online_booking_link,
      }),
    },
    calls_and_messaging: {
      first_message: updated.first_message || '',
      client_outside_door: updated.client_outside_door || '',
      client_running_late: updated.client_running_late || '',
    },
    pharmacy_records: {
      previous_dental_records: updated.previous_dental_records || '',
      share_dental_records: updated.share_dental_records || '',
      patient_articles: updated.patient_articles || '',
      share_patient_prescription: updated.share_patient_prescription || '',
      patient_prescription_recommendation:
        updated.patient_prescription_recommendation || '',
    },
    general_information: {
      ...originalGeneralInfo,
      website_link: updated.website_link || '',
      faqs_link: updated.faqs_link || '',
      accept_new_patients:
        updated.accept_new_patients !== undefined
          ? updated.accept_new_patients
          : false,
      age_restrictions: updated.age_restrictions || '',
      service_details: updated.service_details || '',
      uniqueness: updated.uniqueness || '',
      ease_patient_experience: updated.ease_patient_experience || '',
      address_details: [
        {
          address: updated.address_details_address || '',
          location_tip: updated.address_details_location_tip || '',
          parking_tip: updated.address_details_parking_tip || '',
        },
      ],
      business_open_days: updated.business_open_days || {},
      business_working_hours: updated.business_working_hours || {},
      business_contact_numbers: [updated.business_contact_number || ''],
      billing_contact_details: {
        name: updated.billing_contact_details_name || '',
        email: updated.billing_contact_details_email || '',
        phone_number: updated.billing_contact_details_phone || '',
      },
    },
    faqs: {
      sales_pitch_new_patient: (formvalues && formvalues.sales_pitch_new_patient) || updated.sales_pitch_new_patient || '',
      anxious_patient: (formvalues && formvalues.anxious_patient) || updated.anxious_patient || '', 
      what_to_bring:
        (formvalues && formvalues.what_to_bring) || updated.what_to_bring || '',
      after_appointment_issue:
        (formvalues && formvalues.after_appointment_issue) ||
        updated.after_appointment_issue ||
        '',
      after_appointment_care:
        (formvalues && formvalues.after_appointment_care) ||
        updated.after_appointment_care ||
        '',
      child_first_visit:
        (formvalues && formvalues.child_first_visit) ||
        updated.child_first_visit ||
        '',
    },
    general: {
      ...originalGeneral,
      child_first_visit: {
        min_age: updated.child_first_visit_min_age || '',
        description: updated.child_first_visit_description || '',
      },
      sales_pitch_new_patient: updated.sales_pitch_new_patient || '',
      anxious_patient: updated.anxious_patient || '',
      what_to_bring: updated.what_to_bring || '',
      walk_ins: {
        accepted: updated.walk_ins_available || false,
        description: updated.walk_ins || '',
      },
      patient_form_link: updated.patient_form_link || '',
    },
    services: {
      ...originalServices,
      amenities: [
        {
          type: 'DISABLED_FRIENDLY',
          available: updated.amenities_disabled_friendly_available || false,
          description: updated.amenities_disabled_friendly_description || '',
        },
        {
          type: 'WIFI',
          available: updated.amenities_wifi_available || false,
          description: updated.amenities_wifi_description || '',
        },
        {
          type: 'GAS_ANESTHETIC',
          available: updated.amenities_gas_anesthetic_available || false,
          description: updated.amenities_gas_anesthetic_description || '',
        },
        {
          type: 'REFRESHMENTS',
          available: updated.amenities_refreshments_available || false,
          description: updated.amenities_refreshments_description || '',
        },
      ],
    },
  };

  encodedData.id = {
    ...originalId,
    billing: encodedData.billing,
    faqs: encodedData.faqs,
    custom_requests: encodedData.custom_requests,
    emergency_notification: encodedData.emergency,
    general_enquiries: encodedData.general,
    amenities: encodedData.services?.amenities || originalId.amenities || [],
    general_information: encodedData.general_information,
    calls_and_messaging: encodedData.calls_and_messaging,
    notifications: encodedData.notifications,
    pharmacy_records: encodedData.pharmacy_records,
    scheduling: encodedData.scheduling,
    insurance: encodedData.insurance,
  };
  encodedData.preferences = {
    ...originalPreferences,
    send_staff_info: updated.send_staff_info,
    vm_time_out: updated.vm_time_out,
    custom_post_booking_instructions: (() => {
      const value = updated.custom_post_booking_instructions;
      if (typeof value === 'string') {
        if (!value || value.trim() === '') {
          return null;
        }
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      }
      return value;
    })(),
  };
  return encodedData;
};

const decodeData = (current_preferences) => {
  const { id, client_notification_preferences, preferences } = current_preferences;
  let p = id;
  const decodedData = {
    appointments_walk_in: p.scheduling.appointments_walk_in,
    accepted_insurances: p.insurance.accepted_insurances,
    un_accepted_insurances: p.insurance.un_accepted_insurances,
    collect_insurance: p.insurance.collect_insurance,
    no_insurance_info: p.insurance.no_insurance_info,
    insurance_info: p.insurance.insurance_info,
    cancel_appt_within_duration: p.scheduling.cancel_appt_within_duration,
    cancellation_charges: p.scheduling.cancellation_charges,
    cancellation_duration: p.scheduling.cancellation_duration,
    cancellation_policy:
      p.scheduling.cancellation_policy ||
      current_preferences?.scheduling?.cancellation_policy,
    min_time_to_booking: p.scheduling.min_time_to_booking,
    new_patient_form_link: p.scheduling.new_patient_form_link,
    post_booking_instructions: p.scheduling.post_booking_instructions,
    reschedule_appt_within_24hrs: p.scheduling.reschedule_appt_within_24hrs,
    reschedule_charges: p.scheduling.reschedule_charges,
    reschedule_duration: p.scheduling.reschedule_duration,
    average_booking_value: p.scheduling?.average_booking_value,
    can_reschedule_booking_online: p.scheduling?.can_reschedule_booking_online,
    can_cancel_booking_online: p.scheduling?.can_cancel_booking_online,
    online_booking_link: p.scheduling?.online_booking_link,
    custom_requests: p.custom_requests,
    paymentTypes: p.billing.payment_types,
    insurances_in_network: p.billing.insurances_in_network,
    insuranceTypes: p.billing.insurance_types,
    specialOffers: p.billing.special_offer,
    billing_related: p.billing.billing_related,
    new_patient_consultation_billing:
      p.billing.new_patient_consultation_billing,
    cleaning_cost: p.billing.cleaning_cost,
    what_to_bring: p.faqs.what_to_bring,
    sales_pitch_new_patient: p.faqs.sales_pitch_new_patient,
    anxious_patient: p.faqs.anxious_patient, 
    after_appointment_issue: p.faqs.after_appointment_issue,
    after_appointment_care: p.faqs.after_appointment_care,
    child_first_visit: p.faqs.child_first_visit,
    publicContactPhones: p.notifications.public_contact_phones,
    publicContactEmails: p.notifications.public_contact_email,
    notifyContactPhones: p.notifications.notify_contact_phones,
    notifyContactEmails: p.notifications.notify_contact_email,
    previous_dental_records: p.pharmacy_records.previous_dental_records,
    share_dental_records: p.pharmacy_records.share_dental_records,
    patient_articles: p.pharmacy_records.patient_articles,
    share_patient_prescription: p.pharmacy_records.share_patient_prescription,
    patient_prescription_recommendation:
      p.pharmacy_records.patient_prescription_recommendation,
    notification_mode: client_notification_preferences.notification_channels,
    notification_email: client_notification_preferences.notification_emails,
    notification_phone: client_notification_preferences.notification_phones,
    receive_immediately: client_notification_preferences.receive_immediately,
    website_link: p.general_information.website_link,
    faqs_link: p.general_information.faqs_link,
    accept_new_patients: p.general_information.accept_new_patients,
    age_restrictions: p.general_information.age_restrictions,
    service_details: p.general_information.service_details,
    uniqueness: p.general_information.uniqueness,
    ease_patient_experience: p.general_information.ease_patient_experience,
    first_message: p.calls_and_messaging.first_message,
    business_contact_number: p.general_information.business_contact_numbers[0],
    billing_contact_details_name: p.general_information.billing_contact_details
      ? p.general_information.billing_contact_details.name
      : '',
    billing_contact_details_email: p.general_information.billing_contact_details
      ? p.general_information.billing_contact_details.email
      : '',
    billing_contact_details_phone: p.general_information.billing_contact_details
      ? p.general_information.billing_contact_details.phone_number
      : '',
    business_open_days: p.general_information.business_open_days,
    business_working_hours: p.general_information.business_working_hours,
    client_outside_door: p.calls_and_messaging.client_outside_door,
    address_details: p.general_information.address_details || [{}],
    address_details_address:
      p.general_information.address_details &&
      p.general_information.address_details.length
        ? p.general_information.address_details[0].address
        : '',
    address_details_location_tip:
      p.general_information.address_details &&
      p.general_information.address_details.length
        ? p.general_information.address_details[0].location_tip
        : '',
    address_details_parking_tip:
      p.general_information.address_details &&
      p.general_information.address_details.length
        ? p.general_information.address_details[0].parking_tip
        : '',
    client_running_late: p.calls_and_messaging.client_running_late,
    reschedule_appt_within_duration:
      p.scheduling.reschedule_appt_within_duration,
    service_mapping: p.scheduling.service_mapping || {},
    staff_schedule_mapping: p.scheduling.staff_schedule_mapping || {},
    child_first_visit_min_age: (
      (p.general_enquiries || {}).child_first_visit || {}
    ).min_age,
    child_first_visit_description: (
      (p.general_enquiries || {}).child_first_visit || {}
    ).description,
    walk_ins_available: ((p.general_enquiries || {}).walk_ins || {}).accepted,
    walk_ins: ((p.general_enquiries || {}).walk_ins || {}).description,
    patient_form_link: (p.general_enquiries || {}).patient_form_link,
    sales_enquiry_phones:
      (((p.notifications || {}).enquiries || {}).sales_enquiry || {}).phones ||
      [],
    sales_enquiry_emails:
      (((p.notifications || {}).enquiries || {}).sales_enquiry || {}).emails ||
      [],
    misc_enquiry_phones:
      (((p.notifications || {}).enquiries || {}).misc_enquiry || {}).phones ||
      [],
    misc_enquiry_emails:
      (((p.notifications || {}).enquiries || {}).misc_enquiry || {}).emails ||
      [],
    amenities_disabled_friendly_available:
      (
        (p.amenities || []).find((a) => {
          return a.type === 'DISABLED_FRIENDLY';
        }) || {}
      ).available || false,
    amenities_disabled_friendly_description:
      (
        (p.amenities || []).find((a) => {
          return a.type === 'DISABLED_FRIENDLY';
        }) || {}
      ).description || '',
    amenities_wifi_available:
      (
        (p.amenities || []).find((a) => {
          return a.type === 'WIFI';
        }) || {}
      ).available || false,
    amenities_wifi_description:
      (
        (p.amenities || []).find((a) => {
          return a.type === 'WIFI';
        }) || {}
      ).description || '',
    amenities_gas_anesthetic_available:
      (
        (p.amenities || []).find((a) => {
          return a.type === 'GAS_ANESTHETIC';
        }) || {}
      ).available || false,
    amenities_gas_anesthetic_description:
      (
        (p.amenities || []).find((a) => {
          return a.type === 'GAS_ANESTHETIC';
        }) || {}
      ).description || '',
    amenities_refreshments_available:
      (
        (p.amenities || []).find((a) => {
          return a.type === 'REFRESHMENTS';
        }) || {}
      ).available || false,
    amenities_refreshments_description:
      (
        (p.amenities || []).find((a) => {
          return a.type === 'REFRESHMENTS';
        }) || {}
      ).description || '',
    send_staff_info: preferences.send_staff_info,
    vm_time_out: preferences.vm_time_out,
    custom_post_booking_instructions: (() => {
      const value = preferences.custom_post_booking_instructions;
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'string') {
        return value;
      }
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value, null, 2);
        } catch {
          return '';
        }
      }
      return String(value);
    })(),
  };
  return decodedData;
};

const validate = (preferences, actionIndex) => {
  const errors = {};
  if (!preferences.address_details_address.trim() && actionIndex === 0) {
    errors.address_details_address = {
      message: 'Please enter practice address',
      section: 'General Information',
    };
  }

  if (
    preferences.website_link &&
    !utils.isValidURL(preferences.website_link) &&
    actionIndex === 0
  ) {
    errors.website_link = {
      message: 'Please enter a valid URL',
      section: 'General Information',
    };
  }

  if (
    preferences.faqs_link &&
    !utils.isValidURL(preferences.faqs_link) &&
    actionIndex === 0
  ) {
    errors.faqs_link = {
      message: 'Please enter a valid URL',
      section: 'General Information',
    };
  }

  // if (!preferences.address_details_address) {
  //   errors['address_details_address'] = {
  //     message: 'Please enter your address',
  //   };
  // } else if (
  //   preferences.address_details_address && preferences.address_details_address.trim()
  // ) {
  //   errors['address_details_address'] = {
  //     message: 'Please enter your address',
  //   };
  // }
  // if (
  //   !preferences.business_contact_numbers ||
  //   !preferences.business_contact_numbers.length ||
  //   !preferences.business_contact_numbers[0]
  // ) {
  //   errors['business_contact_numbers.0'] = {
  //     message: 'Please enter a phone number',
  //   };
  // } else if (preferences.business_contact_numbers && preferences.business_contact_numbers.length) {
  //   preferences.business_contact_numbers.map((phone, index) => {
  //     if (phone && !utils.isValidPhone(phone)) {
  //       errors[`business_contact_numbers.${index}`] = {
  //         message: 'Please enter a valid phone number',
  //       };
  //     }

  //     return phone;
  //   });
  // }

  if (!preferences.billing_contact_details_name && actionIndex === 0) {
    errors['billing_contact_details_name'] = {
      message: 'Please enter contact name',
      section: 'General Information',
    };
  }

  if (!preferences.billing_contact_details_email && actionIndex === 0) {
    errors['billing_contact_details_email'] = {
      message: 'Please enter contact email',
      section: 'General Information',
    };
  } else if (
    !utils.isValidEmail(preferences.billing_contact_details_email) &&
    actionIndex === 0
  ) {
    errors['billing_contact_details_email'] = {
      message: 'Please enter a valid email',
      section: 'General Information',
    };
  }

  if (!preferences.billing_contact_details_phone && actionIndex === 0) {
    errors['billing_contact_details_phone'] = {
      message: 'Please enter contact phone',
      section: 'General Information',
    };
  } else if (
    preferences.billing_contact_details_phone &&
    !utils.isValidPhone(preferences.billing_contact_details_phone) &&
    actionIndex === 0
  ) {
    errors['billing_contact_details_phone'] = {
      message: 'Please enter a valid phone number',
      section: 'General Information',
    };
  }
  if (!preferences.first_message.trim() && actionIndex === 1) {
    errors.first_message = {
      message: 'Please enter a message',
      section: 'Missed Calls',
    };
  }

  if (!preferences.notification_mode && actionIndex === 1) {
    errors.notification_mode = {
      message: 'Please select a notification mode',
    };
  }

  if (
    (preferences.notification_mode.includes('SMS') ||
      preferences.notification_mode.includes('PHONE')) &&
    !preferences.notification_email &&
    actionIndex === 2
  ) {
    errors.notification_email = {
      message: 'Please enter an email',
      section: 'Notifications',
    };
  } else if (
    (preferences.notification_mode.includes('SMS') ||
      preferences.notification_mode.includes('PHONE')) &&
    !utils.isValidMultipleEmails(preferences.notification_email) &&
    actionIndex === 2
  ) {
    errors.notification_email = {
      message: 'Please enter a valid email',
      section: 'Notifications',
    };
  }

  if (
    (preferences.notification_mode.includes('SMS') ||
      preferences.notification_mode.includes('PHONE')) &&
    !preferences.notification_phone &&
    actionIndex === 2
  ) {
    errors.notification_phone = {
      message: 'Please enter a phone number',
      section: 'Notifications',
    };
  } else if (
    (preferences.notification_mode.includes('SMS') ||
      preferences.notification_mode.includes('PHONE')) &&
    !utils.isValidPhone(preferences.notification_phone[0]) &&
    actionIndex === 2
  ) {
    errors.notification_phone = {
      message: 'Please enter a valid phone number',
      section: 'Notifications',
    };
  }

  if (
    preferences.reschedule_charges &&
    actionIndex === 3 &&
    !/^\$[0-9.]+|[0-9.]+%$/.test(preferences.reschedule_charges)
  ) {
    errors.reschedule_charges = {
      message: 'Invalid reschedule charge',
      section: 'Scheduling',
    };
  }

  if (
    preferences.cancellation_charges &&
    actionIndex === 3 &&
    !/^\$[0-9.]+|[0-9.]+%$/.test(preferences.cancellation_charges)
  ) {
    errors.cancellation_charges = {
      message: 'Invalid cancellation charge',
      section: 'Scheduling',
    };
  }

  if (
    preferences.online_booking_link &&
    !utils.isValidURL(preferences.online_booking_link) &&
    actionIndex === 3
  ) {
    errors.online_booking_link = {
      message: 'Please enter a valid URL',
      section: 'Scheduling',
    };
  }

  if (
    preferences.cancel_appt_within_duration &&
    preferences?.cancellation_policy?.trim().length < 1 &&
    actionIndex === 3
  ) {
    errors.cancellation_policy = {
      message: 'Please enter the cancellation policy',
      section: 'Scheduling',
    };
  }

  preferences.specialOffers &&
    preferences.specialOffers.forEach((s) => {
      if (s.patient_type == null && actionIndex === 4) {
        errors.specialOffers.patient_type = {
          message: 'Please select one of the option',
          section: 'Patient Billing',
        };
      }
    });

  preferences.specialOffers &&
    preferences.specialOffers.forEach((s) => {
      if (s.description?.trim().length < 1 && actionIndex === 4) {
        errors.specialOffers.description = {
          message: 'Please enter the description',
          section: 'Patient Billing',
        };
      }
    });

  if (preferences.publicContactPhones.length > 0 && actionIndex === 2) {
    let emergencyerrors = preferences.publicContactPhones.filter(
      (phone) => utils.isValidPhone(phone) === false,
    );
    if (emergencyerrors.length > 0) {
      errors.publicContactPhones = {
        message: 'Please enter valid phone number',
        section: 'Notification',
      };
    }
  }
  if (preferences.publicContactEmails.length > 0 && actionIndex === 2) {
    let emergencyerrors = preferences.publicContactEmails.filter(
      (email) => utils.isValidEmail(email) === false,
    );
    if (emergencyerrors.length > 0) {
      errors.publicContactEmails = {
        message: 'Please enter valid email',
        section: 'Notification',
      };
    }
  }
  if (
    preferences.custom_post_booking_instructions &&
    typeof preferences.custom_post_booking_instructions === 'string'
  ) {
    const validateJSON = (value) => {
      if (!value || value.trim() === '') {
        return { isValid: true };
      }

      const trimmedValue = value.trim();

      if (!trimmedValue.startsWith('{') || !trimmedValue.endsWith('}')) {
        return {
          isValid: false,
          error: 'Must be a valid JSON object (e.g., {"key": "value"})'
        };
      }

      try {
        const parsed = JSON.parse(trimmedValue);

        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          return {
            isValid: false,
            error: 'Must be a valid JSON object'
          };
        }

        return { isValid: true };
      } catch (error) {
        return {
          isValid: false,
          error: 'Contains invalid JSON syntax'
        };
      }
    };

    const jsonValidation = validateJSON(preferences.custom_post_booking_instructions);
    if (!jsonValidation.isValid) {
      errors.custom_post_booking_instructions = {
        message: jsonValidation.error,
      };
    }
  }

  return errors;
};

const isMandatoryComplete = () => {
  return true;
};

export default class Preferences extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      preferences: {},
      errors: {},
      loading: true,
      statusModalVisibility: false,
      isSaving: false,
      accordion: -1,
    };

    this.customStyles = {
      menu: (provided) => ({
        ...provided,
        zIndex: 3,
      }),
      control: (provided) => ({
        ...provided,
        minHeight: '50px',
        borderColor: '#cfd0d1',
      }),
    };
    
    this.vmtimeoutDurationOptions = [
      {
        label: '10 mins',
        value: 'PT10M',
      },
      {
        label: '20 mins',
        value: 'PT20M',
      },
      {
        label: '30 min',
        value: 'PT30M',
      },
      {
        label: '1 hour',
        value: 'PT1H',
      },
    ];
    
    this.formRefs = {};
    this.openAccordion = this.openAccordion.bind(this);
    this.onPreferencesFormSubmit = this.onPreferencesFormSubmit.bind(this);
    this.onVerifyStatusModalClose = this.onVerifyStatusModalClose.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.HandleNotificationSetting = this.HandleNotificationSetting.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    this.handleCustomPreferenceAdd = this.handleCustomPreferenceAdd.bind(this);
    this.setRef = this.setRef.bind(this);
  }

  componentDidMount() {
    ReactGA.pageview(window.location.hash);
    ReactGA.event({
      category: 'User',
      action: 'Click on Preference Tab(Dental)',
      label: 'Preference(Dental)  Page',
    });
    this.props.getDentalPreferences();
  }

  openAccordion = (index) => {
    if (index !== this.state.accordion) {
      this.setState({
        accordion: index,
      });
    } else {
      this.setState({
        accordion: -1,
      });
    }
  };

  isEquivalent = (a, b) => {
    // Create arrays of property names
    var aProps = Object.getOwnPropertyNames(a);
    var bProps = Object.getOwnPropertyNames(b);

    // If number of properties is different,
    // objects are not equivalent
    if (aProps.length != bProps.length) {
      return false;
    }

    for (var i = 0; i < aProps.length; i++) {
      var propName = aProps[i];

      // If values of same property are not equal,
      // objects are not equivalent
      if (a[propName] !== b[propName]) {
        return false;
      }
    }

    // If we made it this far, objects
    // are considered equivalent
    return true;
  };

  componentWillReceiveProps(nextProps) {
    if (
      typeof nextProps.preferences.isPreferenceSaved === 'boolean' &&
      nextProps.preferences.isPreferenceSaved !==
        this.props.preferences.isPreferenceSaved &&
      nextProps.preferences.isPreferenceSaved
    ) {
      this.setState(
        {
          isSaving: false,
          statusModalVisibility: true,
        },
        () => {
          setTimeout(this.onVerifyStatusModalClose, 1000);
        },
      );
    }

    if (
      this.props.preferences.isFetchingPreferences !==
        nextProps.preferences.isFetchingPreferences &&
      nextProps.preferences.isFetchingPreferences == false &&
      nextProps.preferences.details &&
      Object.keys(this.state.preferences).length !==
        Object.keys(nextProps.preferences.details)
    ) {
      const newPreferences = decodeData(nextProps.preferences.details);
      this.setState({
        preferences: Object.assign({}, this.state.preferences, newPreferences),
        loading: false,
      });
    }
    if (
      this.props.preferences.isPreferencesaving !==
        nextProps.preferences.isPreferencesaving &&
      nextProps.preferences.isPreferencesaving == false &&
      nextProps.preferences.details &&
      Object.keys(this.state.preferences).length !==
        Object.keys(nextProps.preferences.details)
    ) {
      const newPreferences = decodeData(nextProps.preferences.details);
      this.setState({
        preferences: Object.assign({}, this.state.preferences, newPreferences),
        loading: false,
      });
    }
  }

  onCancelClick = () => {
    const newPreferences = decodeData(this.props.preferences.details);
    this.setState({
      preferences: Object.assign({}, this.state.preferences, newPreferences),
      loading: false,
      errors: {},
      accordion: -1,
    });
  };
  onVerifyStatusModalClose() {
    this.setState({
      statusModalVisibility: false,
    });
  }

  onPreferencesFormSubmit(preference, formvalues, actionIndex) {
    if (!this.props.preferences || !this.props.preferences.details) {
      return;
    }
    if (!preference) {
      preference = this.state.preferences;
    }

    if (!formvalues) {
      formvalues = this.state.preferences;
    }
    try {
      const preferences = encodeData(
        this.props.preferences.details,
        preference,
        formvalues,
      );

      const decodedPref = decodeData(preferences);

      const validationErrors = validate(decodedPref, actionIndex);

      if (Object.keys(validationErrors).length) {
        this.setState({
          errors: validationErrors,
          isSaving: false,
        });
      } else {
        this.setState(
          {
            isSaving: true,
          },
          () => {
            this.props.setDentalPreferences({ preferences });
          },
        );
      }
    } catch (error) {
      this.setState({
        isSaving: false,
      });
    }
  }

  scrollIntoError = (firstErrorKey) => {
    if (firstErrorKey !== 'billing_contact_details_phone') {
      setTimeout(() => {
        this.formRefs[firstErrorKey].scrollIntoView();
      }, 10);
    } else {
      firstErrorKey = 'billing_contact_details_email';
      setTimeout(() => {
        this.formRefs[firstErrorKey].scrollIntoView();
      }, 10);
    }
  };

  setRef(target, refName) {
    if (!target) return;

    if (refName) {
      this.formRefs[refName] = target;
    } else {
      this.formRefs[target.name] = target;
    }
  }

  HandleNotificationSetting(event) {
    const target = event.target;
    const name = target.name;
    const value = target.value;
    const current_state = [...this.state.preferences.receive_immediately];
    if (current_state.includes(value)) {
      current_state.splice(current_state.indexOf(value), 1);
    } else {
      current_state.push(value);
    }
    const preferences = Object.assign({}, this.state.preferences, {
      [name]: current_state,
    });

    const errors = Object.assign({}, this.state.errors, {
      [event.target.fieldName
        ? `${name}.${event.target.fieldName}`
        : name]: null,
    });

    const validationErrors = validate(preferences);
    this.setState({ preferences, errors: validationErrors });
  }

  handleInputChange(event) {
    const target = event.target;
    const value =
      target && target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    if (
      (name === 'average_booking_value' && value.length < 6) ||
      name !== 'average_booking_value'
    ) {
      const arrayvalus = ['notification_email', 'notification_phone'];
      const isArrayValue = arrayvalus.includes(name);
      const preferences = Object.assign({}, this.state.preferences, {
        ...(isArrayValue && { [name]: [value] }),
        ...(!isArrayValue && { [name]: value }),
      });
      const errors = Object.assign({}, this.state.errors, {
        [event.target.fieldName
          ? `${name}.${event.target.fieldName}`
          : name]: null,
      });
      const validationErrors = validate(preferences);
      this.setState({ preferences, errors: validationErrors });
    }
  }

  handleInputBlur(event) {
    const preferences = Object.assign({}, this.state.preferences);

    // Fill phone number in billing contact info, if something is entered in business_contact_numbers and billing_contact_details.phone_number is empty
    if (
      event.target.name === 'business_contact_numbers.0' &&
      !preferences.billing_contact_details.phone_number
    ) {
      preferences.billing_contact_details = Object.assign(
        {},
        preferences.billing_contact_details,
        {
          phone_number: event.target.value,
        },
      );

      this.setState({ preferences });
    }
  }

  handleCustomPreferenceAdd(preference) {
    const preferences = Object.assign({}, this.state.preferences);

    const uuid = utils.generateUUID();
    const customRequests = Object.assign({}, preferences.custom_requests, {
      [uuid]: preference,
    });

    preferences.custom_requests = customRequests;

    this.setState(
      {
        preferences,
      },
      () => {
        this.onPreferencesFormSubmit(preferences, this.state.preferences);
      },
    );
  }

  handleHiddenPreferencesChange = (event) => {
    const { name, value, type, checked } = event.target;
    const updatedPreferences = JSON.parse(
      JSON.stringify(this.state.preferences),
    );

    if (type === 'checkbox') {
      if (name === 'scheduling') {
        if (!updatedPreferences.scheduling) {
          updatedPreferences.scheduling = {};
        }
        updatedPreferences.scheduling = {
          ...updatedPreferences.scheduling,
          ...value,
        };
      } else {
        updatedPreferences[name] = checked;
      }
    } else if (name === 'scheduling') {
      if (!updatedPreferences.scheduling) {
        updatedPreferences.scheduling = {};
      }
      Object.keys(value).forEach((key) => {
        if (value[key] !== undefined) {
          updatedPreferences.scheduling[key] = value[key];
        }
      });
    } else if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value)
    ) {
      if (!updatedPreferences[name]) {
        updatedPreferences[name] = {};
      }
      updatedPreferences[name] = { ...updatedPreferences[name], ...value };
    } else {
      updatedPreferences[name] = value;
    }
    const validationErrors = validate(updatedPreferences);
    this.setState({ preferences: updatedPreferences, errors: validationErrors });
  };

  static getSelectedOption(options, value) {
    return options.find((option) => option.value === value) || null;
  }

  render() {
    const { preferences, currentDomain, auth, softwares } = this.props;
    const { schema } = preferences;
    const { errors, statusModalVisibility } = this.state;
    const csroles = ['CS_ADMIN', 'CS_SETUP', 'CS_SALES'];
    const isCSLogin = csroles.includes(auth.role);
    const features = utils.getCookie('features');
    const calls_and_messaging =
      schema?.dental_preferences?.properties?.calls_and_messaging;
    const notifications = schema?.dental_preferences?.properties?.notifications;
    const scheduling = schema?.dental_preferences?.properties?.scheduling;
    const billing = schema?.dental_preferences?.properties?.billing;
    const amenities = schema?.dental_preferences?.properties?.amenities;
    const faqs = schema?.dental_preferences?.properties?.faqs;
    const pharmacy_records =
      schema?.dental_preferences?.properties?.pharmacy_records;
    const insurance = schema?.dental_preferences?.properties?.insurance;

    if (this.state.loading) {
      return <div className="loading-container">Loading...</div>;
    }
    const selectedVmtimeut = Preferences.getSelectedOption(
      this.vmtimeoutDurationOptions,
      this.state?.preferences?.vm_time_out,
    );
    
    return (
      <div>
        <Prompt
          when={
            !this.isEquivalent(
              decodeData(this.props.preferences.details),
              this.state.preferences,
            )
          }
          message={
            'There are unsaved preferences, are you sure you want to leave without saving?'
          }
        />
        {this.props.preferences?.isFetchingPreferences ? (
          <div className="common-loader">
            <Loading />
          </div>
        ) : (
          <>
            <div>
              <div className="preferences dental-custom webchat_title_list">
                <Form
                  values={this.state.preferences}
                  validate={(touched, values) => {
                    const errors = {};
                    Object.keys(touched).forEach((field) => {
                      /** Validate Notifications */
                      if (field.startsWith('billing_contact_details_email')) {
                        if (!values.billing_contact_details_email) {
                          errors[field] = "Email shouldn't be empty";
                        } else if (
                          !validation.email(
                            values.billing_contact_details_email,
                          )
                        ) {
                          errors[field] = 'Invalid Email';
                        }
                      }

                      if (field.startsWith('notification_email')) {
                        if (!values.notification_email) {
                          errors[field] = "Email shouldn't be empty";
                        } else if (
                          !validation.email(values.notification_email)
                        ) {
                          errors[field] = 'Invalid Email';
                        }
                      }
                      if (field.startsWith('billing_contact_details_phone')) {
                        if (!values.billing_contact_details_phone) {
                          errors[field] = "Phone shouldn't be empty";
                        } else if (
                          !validation.phone(
                            values.billing_contact_details_phone,
                          )
                        ) {
                          errors[field] = 'Invalid Phone';
                        }
                      }

                      if (field === 'business_contact_number') {
                        if (!values.business_contact_number) {
                          errors[field] = "Phone shouldn't be empty";
                        } else if (
                          !validation.phone(values.business_contact_number)
                        ) {
                          errors[field] = 'Invalid Phone';
                        }
                      }
                    });

                    return errors;
                  }}
                >
                  {({ values, onChange, getHandler, errors, hasErrors }) => {
                    return (
                      <>
                        <div
                          className="card__content"
                          style={{ display: 'block' }}
                        >
                          <div
                            className="customization-list"
                            onClick={() => this.openAccordion('0')}
                          >
                            <div className="tabs">
                              <div
                                className={classnames('title', {
                                  'selected-accordion':
                                    this.state.accordion == 0,
                                })}
                              >
                                General Information
                              </div>
                              {/* <div className="description">{custom.description}</div> */}
                            </div>
                            <div
                              className={classnames('acrdn-btn', {
                                'selected-accordion': this.state.accordion == 0,
                              })}
                            >
                              <i className="material-icons">
                                keyboard_arrow_down
                              </i>
                            </div>
                          </div>
                          {this.props.preferences.details &&
                          this.state.accordion == 0 ? (
                            <div className="preferences__content__row">
                              <div
                                className="preferences__content__row__data"
                                ref="acc0"
                              >
                                <Mandatory
                                  values={this.state.preferences}
                                  getHandler={getHandler}
                                  onChange={this.handleInputChange}
                                  errors={this.state.errors}
                                  setRef={this.setRef}
                                  preferences={preferences}
                                />
                                <BusinessProfile
                                  values={this.state.preferences}
                                  getHandler={getHandler}
                                  errors={this.state.errors}
                                  onBlur={this.handleInputBlur}
                                  onChange={this.handleInputChange}
                                  setRef={this.setRef}
                                  preferences={preferences}
                                />
                                <Actions
                                  disabled={this.state.isSaving || hasErrors}
                                  preferences={this.state.preferences}
                                  values={values}
                                  onPreferencesFormSubmit={
                                    this.onPreferencesFormSubmit
                                  }
                                  onCancelClick={this.onCancelClick}
                                  isSaving={this.state.isSaving}
                                  actionIndex={0}
                                />
                              </div>
                            </div>
                          ) : (
                            undefined
                          )}
                          <div
                            className="customization-list"
                            onClick={() => this.openAccordion('1')}
                          >
                            <div className="tabs">
                              <div
                                className={classnames('title', {
                                  'selected-accordion':
                                    this.state.accordion == 1,
                                })}
                              >
                                Missed Calls
                              </div>
                              {/* <div className="description">{custom.description}</div> */}
                            </div>
                            <div
                              className={classnames('acrdn-btn', {
                                'selected-accordion': this.state.accordion == 1,
                              })}
                            >
                              <i className="material-icons">
                                keyboard_arrow_down
                              </i>
                            </div>
                          </div>
                          {this.state.accordion == 1 && (
                            <div className="preferences__content__row">
                              <div className="preferences__content__row__data padd-top">
                                {Array.isArray(features) && !features.includes('VOICE_GREETINGS_HIDDEN') &&
                                  <AddVoiceGreeting
                                    {...this.props}
                                    getHandler={getHandler}
                                  />
                                }
                                {calls_and_messaging?.properties
                                  ?.first_message && (
                                  <div className="mandatory__row">
                                    <p className="mandatory__row__label">
                                      {calls_and_messaging?.properties
                                        ?.first_message?.description ||
                                        `What would you like the first text message to
                                your patient to say?`}
                                      <sup>*</sup>
                                      <br />
                                      <span className="hint-text">
                                        Note: Please don&apos;t enter phone
                                        numbers or URLs as part of this first
                                        message.
                                      </span>
                                    </p>
                                    <div className="mandatory__row__input">
                                      <TextareaCounter
                                        count
                                        maxLength={160}
                                        label=""
                                        name="first_message"
                                        placeholder={'Enter first message'}
                                        value={
                                          this.state.preferences.first_message
                                        }
                                        onChange={this.handleInputChange}
                                        error={this.state.errors.first_message}
                                        className={classnames({
                                          'has-error':
                                            this.state.preferences.first_message.trim()
                                              .length < 1,
                                        })}
                                      />
                                      <span className="message--small message--error">
                                        {this.state.errors.first_message &&
                                          this.state.errors.first_message
                                            .message}
                                      </span>
                                    </div>
                                  </div>
                                )}
                                {calls_and_messaging?.properties
                                  ?.client_outside_door && (
                                  <div className="mandatory__row">
                                    <p className="mandatory__row__label">
                                      {calls_and_messaging?.properties
                                        ?.client_outside_door?.description ||
                                        `How should TrueLark respond if someone needs help getting in?`}
                                    </p>
                                    <div className="mandatory__row__input">
                                      <TextareaCounter
                                        count
                                        maxLength={160}
                                        label=""
                                        name="client_outside_door"
                                        placeholder={
                                          "I'm sorry about that. I've forwarded your message to the practitioner and they'll let you in shortly"
                                        }
                                        value={
                                          this.state.preferences
                                            .client_outside_door
                                        }
                                        onChange={this.handleInputChange}
                                        error={errors.client_outside_door}
                                        className={classnames({
                                          'has-error':
                                            errors.client_outside_door,
                                        })}
                                      />
                                    </div>
                                  </div>
                                )}
                                {calls_and_messaging?.properties
                                  ?.client_running_late && (
                                  <div className="mandatory__row">
                                    <p className="mandatory__row__label">
                                      {calls_and_messaging?.properties
                                        ?.client_running_late?.description ||
                                        `If someone is running late, how would you like TrueLark to respond?`}
                                    </p>
                                    <div className="mandatory__row__input">
                                      <TextareaCounter
                                        count
                                        maxLength={160}
                                        label=""
                                        name="client_running_late"
                                        placeholder={
                                          'Patients are allowed to be upto 5 mins late for an appointment. If you are any later, your appointment may be cancelled and you will have to bear any cancellation costs.'
                                        }
                                        value={
                                          this.state.preferences
                                            .client_running_late
                                        }
                                        onChange={this.handleInputChange}
                                        error={errors.client_running_late}
                                        className={classnames({
                                          'has-error':
                                            errors.client_running_late,
                                        })}
                                      />
                                    </div>
                                  </div>
                                  )}
                                <Actions
                                  disabled={this.state.isSaving}
                                  preferences={this.state.preferences}
                                  values={values}
                                  onPreferencesFormSubmit={
                                    this.onPreferencesFormSubmit
                                  }
                                  onCancelClick={this.onCancelClick}
                                  isSaving={this.state.isSaving}
                                  actionIndex={1}
                                />
                              </div>
                            </div>
                          )}
                          {/* Notifications */}
                          <div
                            className="customization-list"
                            onClick={() => this.openAccordion('2')}
                          >
                            <div className="tabs">
                              <div
                                className={classnames('title', {
                                  'selected-accordion':
                                    this.state.accordion == 2,
                                })}
                              >
                                Notifications
                              </div>
                              {/* <div className="description">{custom.description}</div> */}
                            </div>
                            <div
                              className={classnames('acrdn-btn', {
                                'selected-accordion': this.state.accordion == 2,
                              })}
                            >
                              <i className="material-icons">
                                keyboard_arrow_down
                              </i>
                            </div>
                          </div>
                          {this.props.preferences.details &&
                          this.state.accordion == 2 ? (
                            <div className="preferences__content__row">
                              <div className="preferences__content__row__data">
                                <Notifications
                                  values={this.state.preferences}
                                  vals={values}
                                  onChange={this.handleInputChange}
                                  intentChange={this.HandleNotificationSetting}
                                  errors={this.state.errors}
                                  getHandler={getHandler}
                                  setRef={this.setRef}
                                  intents={this.props.preferences.intents}
                                  auth={this.props.auth}
                                  softwares={this.props.softwares}
                                  notifications={notifications}
                                />
                                <div className="bookings__row">
                                    <p className="bookings__row__label">
                                      <span className="title">How long should TrueLark wait after sending an SMS before notifying your practice that a patient who left a voicemail hasn’t responded?</span>
                                    </p>
                                    <div className="bookings__row__input">
                                      <Select
                                        value={selectedVmtimeut}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.value}
                                        options={this.vmtimeoutDurationOptions}
                                        styles={this.customStyles}
                                        onChange={(e) => this.handleInputChange({
                                          target: {
                                            name: 'vm_time_out',
                                            value: e.value,
                                            type: 'dropdown'
                                          }
                                        })}
                                        disabled={this.state.isSaving}
                                        placeholder="Select Auto VM timeout duration"
                                        isClearable
                                      />
                                      <span className="message--small message--error">
                                        {errors.vm_time_out && errors.vm_time_out.message}
                                      </span>
                                    </div>
                                </div>
                                <Actions
                                  disabled={this.state.isSaving || hasErrors}
                                  preferences={this.state.preferences}
                                  values={values}
                                  onPreferencesFormSubmit={
                                    this.onPreferencesFormSubmit
                                  }
                                  onCancelClick={this.onCancelClick}
                                  isSaving={this.state.isSaving}
                                  actionIndex={2}
                                />
                              </div>
                            </div>
                          ) : (
                            undefined
                          )}
                          {/* Scheduling */}
                          <div
                            className="customization-list"
                            onClick={() => this.openAccordion('3')}
                          >
                            <div className="tabs">
                              <div
                                className={classnames('title', {
                                  'selected-accordion':
                                    this.state.accordion == 3,
                                })}
                              >
                                Scheduling
                              </div>
                              {/* <div className="description">{custom.description}</div> */}
                            </div>
                            <div
                              className={classnames('acrdn-btn', {
                                'selected-accordion': this.state.accordion == 3,
                              })}
                            >
                              <i className="material-icons">
                                keyboard_arrow_down
                              </i>
                            </div>
                          </div>
                          {this.props.preferences.details &&
                          this.state.accordion == 3 ? (
                            <div className="preferences__content__row">
                              <div className="preferences__content__row__data">
                                <DentalBooking
                                  values={this.state.preferences}
                                  onChange={this.handleInputChange}
                                  errors={this.state.errors}
                                  auth={this.props.auth}
                                  softwares={this.props.softwares}
                                  getHandler={getHandler}
                                  setRef={this.setRef}
                                  scheduling={scheduling}
                                />
                                <Actions
                                  disabled={this.state.isSaving || hasErrors}
                                  preferences={this.state.preferences}
                                  values={values}
                                  onPreferencesFormSubmit={
                                    this.onPreferencesFormSubmit
                                  }
                                  onCancelClick={this.onCancelClick}
                                  isSaving={this.state.isSaving}
                                  actionIndex={3}
                                />
                              </div>
                            </div>
                          ) : (
                            undefined
                          )}

                          {/* Client Billing */}
                          <div
                            className="customization-list"
                            onClick={() => this.openAccordion('4')}
                          >
                            <div className="tabs">
                              <div
                                className={classnames('title', {
                                  'selected-accordion':
                                    this.state.accordion == 4,
                                })}
                              >
                                Patient Billing
                              </div>
                              {/* <div className="description">{custom.description}</div> */}
                            </div>
                            <div
                              className={classnames('acrdn-btn', {
                                'selected-accordion': this.state.accordion == 4,
                              })}
                            >
                              <i className="material-icons">
                                keyboard_arrow_down
                              </i>
                            </div>
                          </div>
                          {this.props.preferences.details &&
                          this.state.accordion == 4 ? (
                            <div className="preferences__content__row">
                              <div className="preferences__content__row__data">
                                <ClientBilling
                                  values={values}
                                  preferences={this.state.preferences}
                                  onInputChange={this.handleInputChange}
                                  onChange={onChange}
                                  getHandler={getHandler}
                                  errors={this.state.errors}
                                  paymentTypes={
                                    this.props.preferences.paymentTypes
                                  }
                                  insurances={this.props.preferences.insurances}
                                  recipients={this.props.preferences.recipients}
                                  auth={this.props.auth}
                                  setRef={this.setRef}
                                  billing={billing}
                                />
                                <Actions
                                  disabled={this.state.isSaving || hasErrors}
                                  preferences={this.state.preferences}
                                  values={values}
                                  onPreferencesFormSubmit={
                                    this.onPreferencesFormSubmit
                                  }
                                  onCancelClick={this.onCancelClick}
                                  isSaving={this.state.isSaving}
                                  actionIndex={4}
                                />
                              </div>
                            </div>
                          ) : (
                            undefined
                          )}
                          {/* Client Billing */}
                          <div
                            className="customization-list"
                            onClick={() => this.openAccordion('5')}
                          >
                            <div className="tabs">
                              <div
                                className={classnames('title', {
                                  'selected-accordion':
                                    this.state.accordion == 5,
                                })}
                              >
                                Insurance
                              </div>
                              {/* <div className="description">{custom.description}</div> */}
                            </div>
                            <div
                              className={classnames('acrdn-btn', {
                                'selected-accordion': this.state.accordion == 5,
                              })}
                            >
                              <i className="material-icons">
                                keyboard_arrow_down
                              </i>
                            </div>
                          </div>
                          {this.props.preferences.details &&
                          this.state.accordion == 5 ? (
                            <div className="preferences__content__row">
                              <div className="preferences__content__row__data">
                                <Insurances
                                  preferences={this.state.preferences}
                                  values={values}
                                  onChange={this.handleInputChange}
                                  // onChange={onChange}
                                  setRef={this.setRef}
                                  getHandler={getHandler}
                                  errors={this.state.errors}
                                  paymentTypes={
                                    this.props.preferences?.paymentTypes || []
                                  }
                                  insurance={this.props.preferences.insurance}
                                  recipients={this.props.preferences.recipients}
                                  auth={this.props.auth}
                                  insuranceSchema={insurance}
                                />
                                <Actions
                                  disabled={this.state.isSaving || hasErrors}
                                  preferences={this.state.preferences}
                                  values={values}
                                  onPreferencesFormSubmit={
                                    this.onPreferencesFormSubmit
                                  }
                                  onCancelClick={this.onCancelClick}
                                  isSaving={this.state.isSaving}
                                  actionIndex={5}
                                />
                              </div>
                            </div>
                          ) : (
                            undefined
                          )}

                          {/* Amenities */}
                          <div
                            className="customization-list"
                            onClick={() => this.openAccordion('6')}
                          >
                            <div className="tabs">
                              <div
                                className={classnames('title', {
                                  'selected-accordion':
                                    this.state.accordion == 6,
                                })}
                              >
                                Amenities
                              </div>
                              {/* <div className="description">{custom.description}</div> */}
                            </div>
                            <div
                              className={classnames('acrdn-btn', {
                                'selected-accordion': this.state.accordion == 6,
                              })}
                            >
                              <i className="material-icons">
                                keyboard_arrow_down
                              </i>
                            </div>
                          </div>
                          {this.state.accordion == 6 && (
                            <div className="preferences__content__row">
                              <div className="preferences__content__row__data">
                                <Amenities
                                  values={this.state.preferences}
                                  onChange={this.handleInputChange}
                                  errors={errors}
                                  setRef={this.setRef}
                                  amenities={amenities}
                                />
                                <Actions
                                  disabled={this.state.isSaving || hasErrors}
                                  preferences={this.state.preferences}
                                  values={values}
                                  onPreferencesFormSubmit={
                                    this.onPreferencesFormSubmit
                                  }
                                  onCancelClick={this.onCancelClick}
                                  isSaving={this.state.isSaving}
                                  actionIndex={6}
                                />
                              </div>
                            </div>
                          )}
                          {/* Chats */}
                          <div
                            className="customization-list"
                            onClick={() => this.openAccordion('7')}
                          >
                            <div className="tabs">
                              <div
                                className={classnames('title', {
                                  'selected-accordion':
                                    this.state.accordion == 7,
                                })}
                              >
                                Before and after appointment
                              </div>
                              {/* <div className="description">{custom.description}</div> */}
                            </div>
                            <div
                              className={classnames('acrdn-btn', {
                                'selected-accordion': this.state.accordion == 7,
                              })}
                            >
                              <i className="material-icons">
                                keyboard_arrow_down
                              </i>
                            </div>
                          </div>
                          {this.props.preferences.details &&
                          this.state.accordion == 7 ? (
                            <div className="preferences__content__row">
                              <div className="preferences__content__row__data">
                                <Chats
                                  onChange={onChange}
                                  values={values}
                                  getHandler={getHandler}
                                  errors={this.state.errors}
                                  setRef={this.setRef}
                                  faqs={faqs}
                                />
                                <Actions
                                  disabled={this.state.isSaving || hasErrors}
                                  preferences={this.state.preferences}
                                  values={values}
                                  onPreferencesFormSubmit={
                                    this.onPreferencesFormSubmit
                                  }
                                  onCancelClick={this.onCancelClick}
                                  isSaving={this.state.isSaving}
                                  actionIndex={7}
                                />
                              </div>
                            </div>
                          ) : (
                            undefined
                          )}
                          {/* Pharmacy/ Records */}
                          <div
                            className="customization-list"
                            onClick={() => this.openAccordion('8')}
                          >
                            <div className="tabs">
                              <div
                                className={classnames('title', {
                                  'selected-accordion':
                                    this.state.accordion == 8,
                                })}
                              >
                                Pharmacy Records
                              </div>
                              {/* <div className="description">{custom.description}</div> */}
                            </div>
                            <div
                              className={classnames('acrdn-btn', {
                                'selected-accordion': this.state.accordion == 8,
                              })}
                            >
                              <i className="material-icons">
                                keyboard_arrow_down
                              </i>
                            </div>
                          </div>
                          {this.props.preferences.details &&
                          this.state.accordion == 8 ? (
                            <div className="preferences__content__row">
                              <div className="preferences__content__row__data">
                                <PharmacyRecords
                                  onChange={onChange}
                                  values={this.state.preferences}
                                  getHandler={this.handleInputChange}
                                  errors={this.state.errors}
                                  setRef={this.setRef}
                                  pharmacyRecords={pharmacy_records}
                                />
                                <Actions
                                  disabled={this.state.isSaving || hasErrors}
                                  preferences={this.state.preferences}
                                  values={values}
                                  onPreferencesFormSubmit={
                                    this.onPreferencesFormSubmit
                                  }
                                  onCancelClick={this.onCancelClick}
                                  isSaving={this.state.isSaving}
                                  actionIndex={8}
                                />
                              </div>
                            </div>
                          ) : (
                            undefined
                          )}
                          {/* Advanced */}
                          {Array.isArray(features) && !features.includes('CONTACT_INFO_HIDDEN') &&
                            <div
                              className="customization-list"
                              onClick={() => this.openAccordion('9')}
                            >
                              <div className="tabs">
                                <div
                                  className={classnames('title', {
                                    'selected-accordion':
                                      this.state.accordion == 9,
                                  })}
                                >
                                  Advanced
                                </div>
                                {/* <div className="description">{custom.description}</div> */}
                              </div>
                              <div
                                className={classnames('acrdn-btn', {
                                  'selected-accordion': this.state.accordion == 9,
                                })}
                              >
                                <i className="material-icons">
                                  keyboard_arrow_down
                                </i>
                              </div>
                            </div>}
                          {this.props.preferences.details &&
                          this.state.accordion == 9 ? (
                            <div className="preferences__content__row">
                              <div className="preferences__content__row__data">
                                <Advanced
                                  details={this.state.preferences}
                                  onChange={this.onChange}
                                  onRequestAdd={this.handleCustomPreferenceAdd}
                                  errors={this.state.errors}
                                  setRef={this.setRef}
                                />
                              </div>
                            </div>
                          ) : (
                            undefined
                          )}

                          {/* Hidden Preferences */}
                          {isCSLogin && (
                            <>
                              <div
                                className="customization-list"
                                onClick={() => this.openAccordion('10')}
                              >
                                <div className="tabs">
                                  <div
                                    className={classnames('title', {
                                      'selected-accordion':
                                        this.state.accordion == 10,
                                    })}
                                  >
                                    Hidden Preferences
                                  </div>
                                </div>
                                <div
                                  className={classnames('acrdn-btn', {
                                    'selected-accordion':
                                      this.state.accordion == 10,
                                  })}
                                >
                                  <i className="material-icons">
                                    keyboard_arrow_down
                                  </i>
                                </div>
                              </div>
                              {preferences.details &&
                              this.state.accordion == 10 ? (
                                <div className="preferences__content__row">
                                  <div className="preferences__content__row__data">
                                    <HiddenPreferences
                                      details={this.state.preferences}
                                      onChange={
                                        this.handleHiddenPreferencesChange
                                      }
                                      errors={this.state.errors}
                                      setRef={this.setRef}
                                      disabled={
                                        !isMandatoryComplete(
                                          this.state.preferences,
                                        ) || this.state.isSaving
                                      }
                                      auth={this.props.auth}
                                      preferences={preferences}
                                    />
                                    <Actions
                                      disabled={
                                        this.state.isSaving ||
                                        !isMandatoryComplete(
                                          this.state.preferences,
                                        )
                                      }
                                      onPreferencesFormSubmit={
                                        this.onPreferencesFormSubmit
                                      }
                                      isSaving={this.state.isSaving}
                                      onCancelClick={this.onCancelClick}
                                      actionIndex={10}
                                    />
                                  </div>
                                </div>
                              ) : (
                                undefined
                              )}
                            </>
                          )}
                          {/* Chats */}
                          {/* Form Actions */}
                        </div>
                        {/* {Object.keys(this.state.errors).length > 0 && (
                      <div className="error-message">
                        {
                          this.state.errors[Object.keys(this.state.errors)[0]]
                            .message
                        }
                        <div className="padd-rigt-left">under section</div>
                        {
                          this.state.errors[Object.keys(this.state.errors)[0]]
                            .section
                        }
                      </div>
                    )} */}
                      </>
                    );
                  }}
                </Form>
                {typeof this.props.preferences.isPreferenceSaved ===
                  'boolean' && statusModalVisibility ? (
                  <StatusModal
                    visibility={this.state.statusModalVisibility}
                    status={this.props.preferences.isPreferenceSaved}
                    onClose={this.onVerifyStatusModalClose}
                  />
                ) : (
                  undefined
                )}
              </div>
            </div>

            {/* <div className="advanced--row-dental">
                <div className="">
                  {this.props.preferences.details ? (
                            <Advanced
                              details={this.state.preferences}
                              onChange={this.onChange}
                              onRequestAdd={this.handleCustomPreferenceAdd}
                              errors={this.state.errors}
                              setRef={this.setRef}
                            />): (
                              undefined
                            )}
                    </div>
            </div> */}
            <div className="footer-container">
              <Footnote />
            </div>
          </>
        )}
      </div>
    );
  }
}

Preferences.defaultProps = {
  currentDomain: '',
  getDentalPreferences: () => {},
  setDentalPreferences: () => {},
};

Preferences.propTypes = {
  getDentalPreferences: PropTypes.func,
  setDentalPreferences: PropTypes.func,
  preferences: PropTypes.shape({
    isPreferenceSaved: PropTypes.bool,
    details: PropTypes.shape(),
    paymentTypes: PropTypes.arrayOf(PropTypes.shape()),
    insurances: PropTypes.arrayOf(PropTypes.shape()),
    recipients: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  softwares: PropTypes.shape().isRequired,
};
